<template>
	<div class="body_profitability_analysis">
		<!-- Filtros con el formato del tab y su MISMO estado (plan de filtros,
			2026-10-05): la granularidad y el rango se comparten con
			Rentabilidad, así que cambiarlos acá también los cambia afuera. Las
			categorías son un desplegable por categoría: abrir el de otra la
			vuelve la principal (sin nada tildado = todos sus ítems) y tildar
			UN ítem acota a ese ítem (selección única, Steve 2026-10-07: nunca
			dos a la vez). Van pegadas al borde derecho, así que su lista se abre
			anclada a la derecha: abierta hacia afuera desbordaba el drawer y
			corría todo el contenido. -->
		<div class="filters_row">
			<ReportPeriodFilters
				:filter-period="filterPeriod"
				:filter-options="periodOptions"
				:end-month="endMonth"
				:get-now="getNow"
				range-mode
				:date-range="calendarRange"
				:year-range="yearRange"
				@update-filter-period="SET_FILTER_PERIOD"
				@update-end-month="SET_END_MONTH"
				@update-date-range="setCustomRange"
				@update-year-range="setCustomYearRange" />

			<div v-if="categoryFilters.length" class="category_filters">
				<MultiSelectDropdown
					v-for="cat in categoryFilters"
					:key="cat.id"
					class="category_select"
					:class="{ 'is-principal': cat.id === category }"
					:value="selectedItems[cat.id] || []"
					:options="cat.options"
					:placeholder="cat.label"
					align="right"
					searchable
					single
					@open="onCategoryOpen(cat)"
					@input="(values) => changeCategory({ id: cat.id, values })" />
			</div>
		</div>

		<div v-if="isLoading" class="state_message"><p>Cargando análisis…</p></div>
		<div v-else-if="error" class="state_message"><p class="error_text">{{ error }}</p></div>
		<template v-else-if="data">
			<div class="cards_row" :style="{ '--cards-count': cards.length }">
				<CategoryMetricCard v-for="card in cards" :key="card.key"
					:label="card.label"
					:amount="card.amount"
					:pills="card.pills"
					:items="card.shareItems" />
			</div>
			<p v-if="!cards.length" class="state_message">
				Sin datos de {{ categoryLabel }} para este período.
			</p>

			<!-- Con una selección parcial en el select de la categoría principal
				(una tienda o varias), la card pasa a barras de venta + márgenes
				del agregado de esa selección. -->
			<ProfitabilityCategoryEvolution class="evolution"
				:months="evolutionBuckets"
				:period="period"
				:metrics="evolutionMetrics"
				:item-colors="colorByItem"
				:selection-label="selectionLabel" />
		</template>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import ReportPeriodFilters from "@/components/financialReports/ReportPeriodFilters.vue";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";
import { smartCapitalize } from "@/utils/string";
import CategoryMetricCard from "./CategoryMetricCard.vue";
import ProfitabilityCategoryEvolution from "./ProfitabilityCategoryEvolution.vue";
import { itemColorTokens } from "@/utils/profitabilityItemColors";

// Cuerpo del drawer "Análisis de Categorías" (Rentabilidad): lee el módulo
// `profitabilityAnalysis` y reparte slices a cards presentacionales, como
// BodySalesAnalysis. Sin props: el modal pasa `bodyProps: null`.
//
// La card de Ventas lleva sólo su monto (los badges MoM / YoY se retiraron a
// pedido de Steve, 2026-10-06, y el backend dejó de calcularlos); la de cada
// margen, su % sobre ventas. Las barras reparten ese monto entre los 3 ítems de mayor magnitud
// más "Otros" con el resto acumulado (el recorte lo hace el backend: con una
// docena de tiendas, una barra por cada una era ilegible). El backend manda
// `share` como fracción signada; acá va a 0-100.
const SALES_KEY = "ventas";
// El resto acumulado no es un ítem: neutro propio, fuera de la rueda de
// colores (el backend lo marca con `is_rest`, no por su label).
const REST_COLOR = "var(--progress-bar-otros)";

export default {
	name: "BodyProfitabilityAnalysis",
	components: {
		CategoryMetricCard, MultiSelectDropdown, ProfitabilityCategoryEvolution, ReportPeriodFilters,
	},

	computed: {
		...mapState("profitabilityAnalysis", [
			"data", "isLoading", "error", "category", "selectedItems",
		]),
		...mapGetters("profitabilityAnalysis", ["window"]),
		...mapState("classification", ["categories", "itemsByCategory"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("reportsFilters", [
			"endMonth", "customRange", "customYearRange", "dataFirstMonth",
		]),

		periodOptions() {
			return FILTER_PERIOD_OPTIONS;
		},
		getNow() {
			return moment().toDate();
		},
		// Lo que pintan los triggers de rango: el custom del store o el default
		// del tab, con el mismo recorte al primer mes con datos.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},
		// Firma del rango del tab: cambiarla (acá o afuera) vuelve a pedir.
		windowSignature() {
			const { initDate, endDate, period } = this.window;
			return `${period}|${initDate}|${endDate}`;
		},
		// Granularidad de la respuesta (la del pedido que se está mostrando).
		period() {
			return (this.data && this.data.period) || this.filterPeriod;
		},
		// Un desplegable por categoría configurada: `{ id, label, options }`.
		categoryFilters() {
			return (this.categories || []).map((category) => ({
				id: category,
				label: smartCapitalize(category),
				options: (this.itemsByCategory[category] || []).map((name) => ({
					id: name, label: name,
				})),
			}));
		},

		items() {
			return (this.data && this.data.items) || [];
		},

		// Etiqueta del ítem tildado en el select de la categoría principal
		// (selección única desde 2026-10-07; hasta entonces podían ser varios
		// y la etiqueta era "3 tiendas"). Sin nada tildado, o con el único
		// ítem de una categoría de uno solo (= sin filtro), es null y la
		// evolución dibuja una línea por ítem.
		selectionLabel() {
			const values = (this.selectedItems && this.selectedItems[this.category]) || [];
			const all = (this.itemsByCategory && this.itemsByCategory[this.category]) || [];
			if (values.length !== 1 || all.length <= 1) return null;
			return values[0];
		},

		evolutionBuckets() {
			return (this.data && this.data.evolution && this.data.evolution.buckets) || [];
		},

		evolutionMetrics() {
			return (this.data && this.data.evolution && this.data.evolution.metrics) || [];
		},

		categoryLabel() {
			return (this.data && this.data.category_label) || "la categoría";
		},

		// Token de color por ítem: un color por ítem en todas las cards y la
		// evolución, estable entre rangos (alfabético sobre los ítems
		// configurados de la categoría; los del payload que no lo estén,
		// como «Sin <categoría>», van después).
		colorByItem() {
			const configured = (this.itemsByCategory && this.itemsByCategory[this.category]) || [];
			return itemColorTokens(configured, this.items);
		},

		cards() {
			const cards = (this.data && this.data.cards) || [];
			return cards.map((card) => ({
				key: card.key,
				label: card.label,
				amount: card.amount,
				pills: card.key === SALES_KEY
					? []
					// El % de un escalón se lee sobre las ventas del mes: en una
					// cascada de márgenes no hay otro denominador posible.
					: [{ text: `${Math.round(card.percent)}%`, tone: "accent" }],
				shareItems: (card.items || []).map((item) => ({
					label: item.name,
					percent: Math.round(item.share * 100),
					color: item.is_rest ? REST_COLOR : `var(${this.colorByItem[item.name]})`,
					isRest: Boolean(item.is_rest),
				})),
			}));
		},
	},

	watch: {
		windowSignature() {
			this.refetchProfitabilityAnalysis();
		},
	},

	methods: {
		...mapActions("profitabilityAnalysis", ["refetchProfitabilityAnalysis", "changeCategory"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),

		// Abrir el desplegable de OTRA categoría ya la vuelve principal, sin
		// nada tildado (= todos sus ítems). Sobre la principal no se hace nada
		// (abrir no es cambiar).
		onCategoryOpen(cat) {
			if (cat.id === this.category) return;
			this.changeCategory({ id: cat.id, values: [] });
		},
	},
};
</script>

<style scoped lang="scss">
.body_profitability_analysis {
	padding: 0 20px 24px;
	// Columna flex que ocupa la sección del drawer (`.modal_back_caa`, ya
	// flex:1 + min-height:0): así la card de evolución puede estirarse hasta
	// el alto disponible cuando muestra su tabla. Filtros y cards no se
	// encogen.
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
}

.filters_row {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 16px;
}

.category_filters {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

// La categoría principal (la del análisis) se distingue aunque no tenga nada
// tildado, con el mismo relleno que el desplegable usa al tener ítems
// tildados (`.ms_trigger.is-active`): fondo primario del tema y texto claro.
.category_select.is-principal :deep(.ms_trigger) {
	border-color: var(--report-primary);
	background: var(--report-primary);
	color: var(--report-bg);
}

// Los escalones son una cascada que se lee en fila: una columna por card,
// todas del mismo ancho (nada de `auto-fit`, que con 4 cards y un mínimo alto
// desbordaba el drawer y recortaba la primera). Las columnas siguen a las
// cards que trae el plan: 4 en Productos, 3 en Servicios (con EBITDA), y la
// fila ocupa todo el ancho en ambos casos.
.cards_row {
	flex-shrink: 0;
	display: grid;
	grid-template-columns: repeat(var(--cards-count, 4), minmax(0, 1fr));
	gap: 16px;
	align-items: stretch;
}

@media (max-width: 1180px) {
	.cards_row {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 680px) {
	.cards_row {
		grid-template-columns: minmax(0, 1fr);
	}
}

.evolution {
	margin-top: 16px;
	flex-shrink: 0;
}

// La card avisa con `is-fill` que muestra la tabla: puede encogerse hasta el
// resto del alto del drawer, pero mide lo que su contenido (el scoped del
// padre alcanza al root del hijo).
.evolution.is-fill {
	flex: 0 1 auto;
	min-height: 0;
}

.state_message {
	padding: 32px 0;
	text-align: center;
	color: var(--report-text-muted);
	font-size: 14px;
}

.error_text {
	color: var(--report-negative);
}
</style>
